import React, { useContext, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { BASE_URL_API } from '../../References/urls'
import AuthContext from '../../store/auth-context'
import styles from './AdminPersonnel.module.css'
import AdminNavigator from './AdminNavigator'
import { Search, Plus, Users, Calendar, DollarSign, Activity, ChevronLeft, ChevronRight, ArrowUpDown, TrendingUp, Eye, Filter, Download, Zap } from 'lucide-react'

const AdminPersonnel = () => {
    
    const authCtx = useContext(AuthContext)
    const [isOpen, setIsOpen] = useState(false)
    const [allPages, setAllPages] = useState([])
    const [currentPage, setCurrentPage] = useState(1)
    const [postsPerPage] = useState(20)
    const [search, setSearch] = useState('')
    const [sort, setSort] = useState({ column: null, order: null })
    const [isLoading, setIsLoading] = useState(true)

    // Stats data
    const stats = [
        { title: 'Total Executives', value: allPages.length, icon: Users, trend: '+12%' },
        { title: 'Active Today', value: '24', icon: Activity, trend: '+8%' },
        { title: 'Total Revenue', value: '$45.2K', icon: DollarSign, trend: '+15%' },
        { title: 'This Month', value: '186', icon: TrendingUp, trend: '+23%' }
    ]

    const filteredData = allPages
        .filter(
            (item) =>
                item.username?.toLowerCase().includes(search.toLowerCase()) ||
                item.userID?.toLowerCase().includes(search.toLowerCase()) ||
                (item.createdOn && item.createdOn.toLowerCase().includes(search.toLowerCase()))
        )
        .sort((a, b) => {
            let valueA = a[sort.column];
            let valueB = b[sort.column];

            if (typeof valueA === 'string') {
                valueA = valueA?.toLowerCase();
            }

            if (typeof valueB === 'string') {
                valueB = valueB?.toLowerCase();
            }

            valueA = isNaN(Date.parse(valueA)) ? valueA : new Date(valueA);
            valueB = isNaN(Date.parse(valueB)) ? valueB : new Date(valueB);

            valueA = isNaN(parseFloat(valueA)) ? valueA : parseFloat(valueA);
            valueB = isNaN(parseFloat(valueB)) ? valueB : parseFloat(valueB);

            if (sort.order === 'asc') {
                return valueA > valueB ? 1 : -1;
            } else {
                return valueA < valueB ? 1 : -1;
            }
        });

    const indexOfLastPost = currentPage * postsPerPage;
    const indexOfFirstPost = indexOfLastPost - postsPerPage;
    const currentPages = filteredData.slice(indexOfFirstPost, indexOfLastPost);

    const paginate = (pageNumber) => setCurrentPage(pageNumber);

    const handleSearch = (e) => {
        setSearch(e.target.value);
        setCurrentPage(1);
    };

    const handleSort = (column) => {
        if (sort.column === column) {
            setSort({
                column,
                order: sort.order === 'asc' ? 'desc' : 'asc',
            });
        } else {
            setSort({
                column,
                order: 'asc',
            });
        }
    };

    const adminGetAllExecutives = async () => {
        setIsLoading(true);
        const adminGetAllExecutivesResponse = await fetch(BASE_URL_API+"/adminGetAllExecutives",
        {
            method : 'POST',
            body : JSON.stringify({
                token : authCtx.token
            })
        });

        if(!adminGetAllExecutivesResponse.ok) {
            console.log("Something went wrong : Server Error")
        } else {
            const adminGetAllExecutivesRespo = await adminGetAllExecutivesResponse.json();
            if(adminGetAllExecutivesRespo.status==='success') {
                setAllPages(adminGetAllExecutivesRespo.response)
            } else {
                if(adminGetAllExecutivesRespo.message==='token Expired, please login again!') {
                    authCtx.logout()
                } else {

                }
            }
            console.log(adminGetAllExecutivesRespo)
        }
        setIsLoading(false);
    }

    useEffect(() => {
        adminGetAllExecutives();
    }, [])

    const totalPages = Math.ceil(filteredData.length / postsPerPage);

    return (
        <div className={styles.mainWrapper}>
            <div className={styles.backgroundAnimation}>
                <div className={styles.floatingOrb1}></div>
                <div className={styles.floatingOrb2}></div>
                <div className={styles.floatingOrb3}></div>
            </div>
            
            <AdminNavigator />
            
            <div className={styles.mainContainer}>
                <div className={styles.contentWrapper}>
                    {/* Header Section */}
                    <div className={styles.pageHeader}>
                        <div className={styles.headerContent}>
                            <h1 className={styles.pageTitle}>Personnel Command Center</h1>
                            <p className={styles.pageSubtitle}>Manage and monitor your executive team with style</p>
                        </div>
                        <Link 
                            // onClick={() => setIsOpen(true)}
                            className={styles.addButton}
                            to={`/control/personnel/add`}
                        >
                            <Plus className={styles.addButtonIcon} />
                            <span>Add Executive</span>
                            <div className={styles.buttonOverlay}></div>
                        </Link>
                    </div>

                    {/* Stats Grid */}
                    <div className={styles.statsGrid}>
                        {stats.map((stat, index) => (
                            <div key={index} className={styles.statCard}>
                                <div className={styles.statContent}>
                                    <div className={styles.statInfo}>
                                        <p className={styles.statTitle}>{stat.title}</p>
                                        <p className={styles.statValue}>{stat.value}</p>
                                        <div className={styles.statTrend}>
                                            <TrendingUp className={styles.trendIcon} />
                                            <span>{stat.trend}</span>
                                        </div>
                                    </div>
                                    <div className={styles.statIconWrapper}>
                                        <stat.icon className={styles.statIcon} />
                                    </div>
                                </div>
                                <div className={styles.statOverlay}></div>
                            </div>
                        ))}
                    </div>

                    {/* Main Content Grid */}
                    <div className={styles.contentGrid}>
                        {/* Table Section */}
                        <div className={styles.tableSection}>
                            <div className={styles.tableContainer}>
                                {/* Table Header */}
                                <div className={styles.tableHeaderSection}>
                                    <div className={styles.tableHeaderContent}>
                                        <div className={styles.tableHeaderInfo}>
                                            <h2 className={styles.tableTitle}>
                                                <Users className={styles.tableTitleIcon} />
                                                Executive Dashboard
                                            </h2>
                                            <p className={styles.tableSubtitle}>{filteredData.length} total executives</p>
                                        </div>
                                        
                                        <div className={styles.tableControls}>
                                            <div className={styles.searchWrapper}>
                                                <Search className={styles.searchIcon} />
                                                <input
                                                    type="text"
                                                    placeholder="Search executives..."
                                                    value={search}
                                                    onChange={handleSearch}
                                                    className={styles.searchInput}
                                                />
                                            </div>
                                            <button className={styles.controlButton}>
                                                <Filter className={styles.controlIcon} />
                                            </button>
                                            <button className={styles.controlButton}>
                                                <Download className={styles.controlIcon} />
                                            </button>
                                        </div>
                                    </div>
                                </div>

                                {/* Table Content */}
                                <div className={styles.tableContent}>
                                    {isLoading ? (
                                        <div className={styles.loadingWrapper}>
                                            <Zap className={styles.loadingIcon} />
                                            <span className={styles.loadingText}>Loading executives...</span>
                                        </div>
                                    ) : (
                                        <table className={styles.table}>
                                            <thead>
                                                <tr className={styles.tableHeaderRow}>
                                                    <th className={styles.tableHeaderCell}>
                                                        <button
                                                            onClick={() => handleSort('username')}
                                                            className={styles.sortButton}
                                                        >
                                                            <span>Executive</span>
                                                            <ArrowUpDown className={styles.sortIcon} />
                                                        </button>
                                                    </th>
                                                    <th className={styles.tableHeaderCell}>
                                                        <button
                                                            onClick={() => handleSort('userID')}
                                                            className={styles.sortButton}
                                                        >
                                                            <span>User ID</span>
                                                            <ArrowUpDown className={styles.sortIcon} />
                                                        </button>
                                                    </th>
                                                    <th className={styles.tableHeaderCell}>Bookings</th>
                                                    <th className={styles.tableHeaderCell}>Revenue</th>
                                                    <th className={styles.tableHeaderCell}>Last Visit</th>
                                                    <th className={styles.tableHeaderCell}>Status</th>
                                                    <th className={styles.tableHeaderCell}>Actions</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {currentPages.map((item, index) => (
                                                    <tr key={index} className={styles.tableRow}>
                                                        <td className={styles.tableCell}>
                                                            <div className={styles.executiveInfo}>
                                                                <div className={styles.avatar}>
                                                                    {item.username?.charAt(0) || 'U'}
                                                                </div>
                                                                <div className={styles.executiveDetails}>
                                                                    <div className={styles.executiveName}>{item.username}</div>
                                                                    <div className={styles.executiveRole}>Executive</div>
                                                                </div>
                                                            </div>
                                                        </td>
                                                        <td className={styles.tableCell}>
                                                            <Link
                                                                to={item.userID}
                                                                className={styles.userIdLink}
                                                            >
                                                                {item.userID}
                                                            </Link>
                                                        </td>
                                                        <td className={styles.tableCell}>
                                                            <div className={styles.bookingInfo}>
                                                                <div className={styles.statusDot}></div>
                                                                <span className={styles.bookingCount}>24</span>
                                                            </div>
                                                        </td>
                                                        <td className={styles.tableCell}>
                                                            <span className={styles.revenue}>$12.5K</span>
                                                        </td>
                                                        <td className={styles.tableCell}>
                                                            <span className={styles.lastVisit}>2 hours ago</span>
                                                        </td>
                                                        <td className={styles.tableCell}>
                                                            <span className={styles.statusBadge}>
                                                                Active
                                                            </span>
                                                        </td>
                                                        <td className={styles.tableCell}>
                                                            <button className={styles.actionButton}>
                                                                <Eye className={styles.actionIcon} />
                                                            </button>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    )}
                                </div>

                                {/* Pagination */}
                                {totalPages > 1 && (
                                    <div className={styles.paginationSection}>
                                        <div className={styles.paginationInfo}>
                                            Showing {indexOfFirstPost + 1} to {Math.min(indexOfLastPost, filteredData.length)} of {filteredData.length} executives
                                        </div>
                                        <div className={styles.paginationControls}>
                                            <button
                                                onClick={() => currentPage > 1 && paginate(currentPage - 1)}
                                                disabled={currentPage === 1}
                                                className={styles.paginationButton}
                                            >
                                                <ChevronLeft className={styles.paginationIcon} />
                                            </button>
                                            {[...Array(Math.min(5, totalPages))].map((_, i) => {
                                                const pageNumber = i + 1;
                                                return (
                                                    <button
                                                        key={i}
                                                        onClick={() => paginate(pageNumber)}
                                                        className={`${styles.pageButton} ${
                                                            currentPage === pageNumber ? styles.activePageButton : ''
                                                        }`}
                                                    >
                                                        {pageNumber}
                                                    </button>
                                                );
                                            })}
                                            <button
                                                onClick={() => currentPage < totalPages && paginate(currentPage + 1)}
                                                disabled={currentPage === totalPages}
                                                className={styles.paginationButton}
                                            >
                                                <ChevronRight className={styles.paginationIcon} />
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Sidebar */}
                        <div className={styles.sidebar}>
                            {/* Recent Activity */}
                            <div className={styles.sidebarCard}>
                                <h3 className={styles.sidebarTitle}>
                                    <Activity className={styles.sidebarTitleIcon} />
                                    Recent Activity
                                </h3>
                                <div className={styles.activityList}>
                                    {[
                                        { action: 'New executive joined', time: '2m ago', type: 'success' },
                                        { action: 'Profile updated', time: '1h ago', type: 'info' },
                                        { action: 'Booking completed', time: '3h ago', type: 'primary' },
                                    ].map((activity, index) => (
                                        <div key={index} className={styles.activityItem}>
                                            <div className={`${styles.activityDot} ${styles[activity.type]}`}></div>
                                            <div className={styles.activityContent}>
                                                <p className={styles.activityAction}>{activity.action}</p>
                                                <p className={styles.activityTime}>{activity.time}</p>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* Quick Actions */}
                            <div className={styles.sidebarCard}>
                                <h3 className={styles.sidebarTitle}>Quick Actions</h3>
                                <div className={styles.quickActionsList}>
                                    <button className={styles.quickActionButton}>
                                        Export Data
                                    </button>
                                    <button className={styles.quickActionButton}>
                                        Send Notifications
                                    </button>
                                    <button className={styles.quickActionButton}>
                                        Generate Report
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default AdminPersonnel